// Building blocks for the message files. A message is a string, or a function when it takes
// values: the compiler then checks every language's function takes the same arguments.
import { createElement, Fragment, type ReactNode } from "react";

import { LOCALE_INFO, type Locale } from "./locales";

type PluralForms = Partial<Record<Intl.LDMLPluralRule, string>> & { other: string };

/**
 * The plural form of `n` under the language's rules (French counts 0 as singular, English
 * does not). Each form may use `#` for the number, written in the language's digits.
 */
export function plural(locale: Locale): (n: number, forms: PluralForms) => string {
  const rules = new Intl.PluralRules(LOCALE_INFO[locale].intl);
  const number = new Intl.NumberFormat(LOCALE_INFO[locale].intl);
  return (n, forms) => (forms[rules.select(n)] ?? forms.other).replaceAll("#", number.format(n));
}

/** A sentence with elements in it (a link, a bold name), as one node with no keys needed. */
export function rich(...parts: ReactNode[]): ReactNode {
  return createElement(Fragment, null, ...parts);
}

/** A list joined the language's way: "a, b and c", "a, b et c", "a, b y c". */
export function list(locale: Locale): (items: string[]) => string {
  const format = new Intl.ListFormat(LOCALE_INFO[locale].intl, { type: "conjunction" });
  return (items) => format.format(items);
}
